import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TrustBadge from "@/components/TrustBadge";
import { BadgeCheck, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminVerification() {
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);

  const load = async () => {
    const [d, h, u] = await Promise.all([
      api.get("/doctors", { params: { limit: 100 } }),
      api.get("/hospitals", { params: { limit: 100 } }),
      api.get("/admin/users/pending-verification"),
    ]);
    setDoctors(d.data);
    setHospitals(h.data);
    setPendingUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const verify = async (type, id) => {
    try {
      await api.put(`/admin/verify/${type}/${id}`);
      toast.success("Provider profile verified");
      load();
    } catch { toast.error("Could not verify"); }
  };

  const decideUser = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/verification`, { status });
      toast.success(status === "verified" ? "Credentials approved" : "Credentials rejected");
      load();
    } catch { toast.error("Could not update"); }
  };

  const unverifiedDoctors = doctors.filter((d) => !d.verified);
  const unverifiedHospitals = hospitals.filter((h) => !h.verified);

  return (
    <AppShell pageTitle="Verifications" pageDescription="Approve email-verified professionals and grant Trust badges.">
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="verify-tab-users">User credentials ({pendingUsers.length})</TabsTrigger>
          <TabsTrigger value="doctors" data-testid="verify-tab-doctors">Doctor profiles ({unverifiedDoctors.length})</TabsTrigger>
          <TabsTrigger value="hospitals" data-testid="verify-tab-hospitals">Hospital profiles ({unverifiedHospitals.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          {pendingUsers.length === 0 ? (
            <EmptyState label="No user credentials awaiting review." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pending-users-list">
              {pendingUsers.map((u) => (
                <div key={u.id} className="rounded-lg border border-border bg-white p-5" data-testid={`pending-user-${u.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{u.role}</div>
                      <div className="mt-1 font-medium text-primary truncate">{u.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">{u.email}</div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                        <Cell label="Category" value={humanize(u.category)} />
                        <Cell label={u.role === "doctor" ? "Specialty" : "Type"} value={u.specialty || humanize(u.category)} />
                        <Cell label="Organization" value={u.organization} span={2} />
                        <Cell label="Registration" value={u.registration_number} span={2} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      className="flex-1"
                      onClick={() => decideUser(u.id, "verified")}
                      data-testid={`verify-user-approve-${u.id}`}
                    >
                      <BadgeCheck size={14} className="mr-1.5" /> Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => decideUser(u.id, "rejected")}
                      data-testid={`verify-user-reject-${u.id}`}
                    >
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="doctors" className="mt-4">
          <VerifyList
            items={unverifiedDoctors}
            testidPrefix="verify-doctor"
            renderTitle={(d) => `${d.name} · ${d.specialty}`}
            renderSub={(d) => `${d.city} · ${d.hospital_affiliation || "Independent"}`}
            onVerify={(id) => verify("doctor", id)}
          />
        </TabsContent>
        <TabsContent value="hospitals" className="mt-4">
          <VerifyList
            items={unverifiedHospitals}
            testidPrefix="verify-hospital"
            renderTitle={(h) => h.name}
            renderSub={(h) => `${h.city} · ${h.beds} beds · ${h.accreditation}`}
            onVerify={(id) => verify("hospital", id)}
          />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function VerifyList({ items, renderTitle, renderSub, onVerify, testidPrefix }) {
  if (items.length === 0) {
    return <EmptyState label="No pending verifications right now." />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((it) => (
        <div key={it.id} className="rounded-lg border border-border bg-white p-5" data-testid={`${testidPrefix}-card-${it.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-medium text-primary">{renderTitle(it)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{renderSub(it)}</div>
            </div>
            <TrustBadge tier={it.tier || maturity(it.trust_score)} score={it.trust_score} size="sm" />
          </div>
          <Button className="mt-4 w-full" onClick={() => onVerify(it.id)} data-testid={`${testidPrefix}-verify-${it.id}`}>
            <BadgeCheck size={14} className="mr-1.5" /> Verify
          </Button>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-10 text-center">
      <BadgeCheck size={28} className="mx-auto text-secondary" />
      <div className="mt-3 font-serif text-xl text-primary">All caught up</div>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function Cell({ label, value, span }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-primary font-medium truncate">{value || "—"}</div>
    </div>
  );
}

function humanize(v) {
  if (!v) return "";
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function maturity(score) {
  if (score >= 88) return "platinum";
  if (score >= 78) return "gold";
  if (score >= 65) return "silver";
  return "bronze";
}
